import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ICONS, SPECIALIZATIONS, SOFTWARE } from '../constants';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import SimpleCaptcha from '../components/SimpleCaptcha';
import { API_URL } from '../config';
import { useTranslation } from '../hooks/useTranslation';
import LanguageToggle from '../components/LanguageToggle';
import { ProfileType } from '../types';

const STAFF_TYPES = [
  ProfileType.PRODUCER,
  ProfileType.SUPERVISOR,
  ProfileType.COORDINATOR,
  ProfileType.SALES,
  ProfileType.FINDER,
  ProfileType.ADMIN,
] as const;

type RegistrationType = ProfileType | typeof ProfileType.VFX_ARTIST;

const PublicRegister: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();
  const { t, setLanguage } = useTranslation();

  const isInviteFlow = Boolean(token);

  const [step, setStep] = useState<'type' | 'form' | 'success'>('type');
  const [selectedType, setSelectedType] = useState<RegistrationType | null>(null);

  const [validating, setValidating] = useState(isInviteFlow);
  const [tokenValid, setTokenValid] = useState(!isInviteFlow);
  const [tokenMessage, setTokenMessage] = useState('');
  const [inviteProfileType, setInviteProfileType] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    specialization: '',
    software: [] as string[],
    telegram: '',
    contact: '',
    portfolioUrl: '',
    description: '',
    location: '',
    organization: '',
  });

  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successKind, setSuccessKind] = useState<'artist_card' | 'access_request' | 'invite'>('access_request');

  const validateInviteToken = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/v1/invite/${token}`);
      const data = await response.json();

      if (data.valid) {
        const kind = data.inviteKind || 'vfx_artist';
        if (kind === 'vfx_artist') {
          navigate(`/apply/${token}`, { replace: true });
          return;
        }
        setTokenValid(true);
        setInviteProfileType(data.profileType || (kind === 'client' ? 'client' : 'producer'));
        setStep('form');
        if (data.language) setLanguage(data.language);
      } else {
        setTokenValid(false);
        setTokenMessage(data.message || 'Invalid or expired invite link');
      }
    } catch {
      setTokenValid(false);
      setTokenMessage('Failed to validate invite link');
    } finally {
      setValidating(false);
    }
  }, [token, navigate, setLanguage]);

  useEffect(() => {
    if (isInviteFlow) {
      validateInviteToken();
    }
  }, [isInviteFlow, validateInviteToken]);

  const activeProfileType = isInviteFlow
    ? inviteProfileType || 'producer'
    : selectedType || ProfileType.VFX_ARTIST;

  const isArtistForm = activeProfileType === ProfileType.VFX_ARTIST;

  const handleInputChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSoftwareToggle = (software: string) => {
    setFormData((prev) => ({
      ...prev,
      software: prev.software.includes(software)
        ? prev.software.filter((s) => s !== software)
        : [...prev.software, software],
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatar(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleTypeSelect = (type: RegistrationType) => {
    setSelectedType(type);
    setStep('form');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isInviteFlow && !captchaValid) {
      setError(t('invite.captchaRequired'));
      return;
    }

    if (isInviteFlow) {
      if (formData.password.length < 8) {
        setError(t('register.passwordTooShort'));
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError(t('register.passwordMismatch'));
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      if (isInviteFlow && token) {
        const response = await fetch(`${API_URL}/api/v1/invite/${token}/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            password: formData.password,
            confirmPassword: formData.confirmPassword,
            telegram: formData.telegram || undefined,
            organization: formData.organization || undefined,
          }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Registration failed');
        setSuccessKind('invite');
        setStep('success');
        return;
      }

      const submitData = new FormData();
      submitData.append('profileType', activeProfileType);
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('captchaToken', 'simple-captcha-validated');

      if (isArtistForm) {
        submitData.append('specialization', formData.specialization);
        submitData.append('software', JSON.stringify(formData.software));
        if (formData.contact) submitData.append('contact', formData.contact);
        if (formData.portfolioUrl) submitData.append('portfolioUrl', formData.portfolioUrl);
        if (formData.description) submitData.append('description', formData.description);
        if (formData.location) submitData.append('location', formData.location);
        if (avatar) submitData.append('avatar', avatar);
      } else {
        if (formData.organization) submitData.append('organization', formData.organization);
      }

      if (formData.telegram) submitData.append('telegram', formData.telegram);

      const response = await fetch(`${API_URL}/api/v1/public/register`, {
        method: 'POST',
        body: submitData,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to submit application');

      setSuccessKind(data.kind === 'artist_card' ? 'artist_card' : 'access_request');
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (isInviteFlow && !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <ICONS.AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{t('invite.invalidToken')}</h1>
          <p className="text-gray-400">{tokenMessage}</p>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    const title =
      successKind === 'invite'
        ? t('register.inviteSuccessTitle')
        : successKind === 'artist_card'
          ? t('register.artistSuccessTitle')
          : t('register.successTitle');
    const message =
      successKind === 'invite'
        ? t('register.inviteSuccessMessage')
        : successKind === 'artist_card'
          ? t('register.artistSuccessMessage')
          : t('register.successMessage');

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <ICONS.Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">{title}</h1>
          <p className="text-gray-400 mb-6">{message}</p>
          {!isInviteFlow && (
            <Link to="/" className="text-indigo-400 hover:text-indigo-300 underline">
              {t('register.backToLogin')}
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (!isInviteFlow && step === 'type') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 relative">
            <div className="absolute top-0 right-0">
              <LanguageToggle />
            </div>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <ICONS.BookOpen className="w-10 h-10 text-indigo-400" />
              <h1 className="text-3xl font-bold text-white">VFX SpellBook</h1>
            </div>
            <p className="text-xl text-gray-300">{t('register.hubTitle')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('register.hubSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <button
              type="button"
              onClick={() => handleTypeSelect(ProfileType.VFX_ARTIST)}
              className="bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-indigo-500 rounded-lg p-6 text-left transition-all"
            >
              <h3 className="text-lg font-semibold text-white mb-2">{t('register.types.vfxArtist')}</h3>
              <p className="text-sm text-gray-400">{t('register.types.vfxArtistDesc')}</p>
            </button>

            <button
              type="button"
              onClick={() => handleTypeSelect(ProfileType.CLIENT)}
              className="bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-indigo-500 rounded-lg p-6 text-left transition-all"
            >
              <h3 className="text-lg font-semibold text-white mb-2">{t('register.types.client')}</h3>
              <p className="text-sm text-gray-400">{t('register.types.clientDesc')}</p>
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">{t('register.types.staffGroup')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {STAFF_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleTypeSelect(type)}
                  className="bg-gray-700/50 hover:bg-gray-700 border border-gray-600 hover:border-indigo-500 rounded-lg px-4 py-3 text-left text-white transition-all"
                >
                  {t(`register.types.${type}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/" className="text-sm text-indigo-400 hover:text-indigo-300 underline">
              {t('register.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 px-4 overflow-x-hidden">
      <div className="max-w-3xl mx-auto w-full min-w-0">
        <div className="text-center mb-8 relative">
          <div className="absolute top-0 right-0">
            <LanguageToggle />
          </div>
          <div className="flex items-center justify-center space-x-2 mb-4">
            <ICONS.BookOpen className="w-10 h-10 text-indigo-400" />
            <h1 className="text-3xl font-bold text-white">VFX SpellBook</h1>
          </div>
          <p className="text-xl text-gray-300">
            {isInviteFlow ? t('register.inviteFormTitle') : t('register.formTitle')}
          </p>
          <p className="text-sm text-indigo-400 mt-2">
            {t(`register.types.${activeProfileType}`)}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg shadow-xl p-6 space-y-6 min-w-0 overflow-x-hidden">
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <Input
            label={t('artistForm.name')}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
          />

          <Input
            label={t('artistForm.email')}
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />

          {isInviteFlow && (
            <>
              <Input
                label={t('login.password')}
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
              />
              <Input
                label={t('resetPassword.confirmPassword')}
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                required
              />
            </>
          )}

          {activeProfileType === ProfileType.CLIENT && (
            <Input
              label={t('register.organization')}
              value={formData.organization}
              onChange={(e) => handleInputChange('organization', e.target.value)}
            />
          )}

          <Input
            label={t('artistForm.telegram')}
            value={formData.telegram}
            onChange={(e) => handleInputChange('telegram', e.target.value)}
          />

          {isArtistForm && !isInviteFlow && (
            <>
              <Select
                label={t('artistForm.specialization')}
                value={formData.specialization}
                onChange={(e) => handleInputChange('specialization', e.target.value)}
                required
              >
                <option value="">{t('register.selectSpecialization')}</option>
                {SPECIALIZATIONS.map((spec) => (
                  <option key={spec} value={spec}>{spec}</option>
                ))}
              </Select>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('artistForm.software')}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {SOFTWARE.map((sw) => (
                    <label key={sw} className="flex items-center space-x-2 text-gray-300 text-sm">
                      <input
                        type="checkbox"
                        checked={formData.software.includes(sw)}
                        onChange={() => handleSoftwareToggle(sw)}
                        className="rounded border-gray-600"
                      />
                      <span>{sw}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Input
                label={t('artistForm.portfolioUrl')}
                value={formData.portfolioUrl}
                onChange={(e) => handleInputChange('portfolioUrl', e.target.value)}
              />

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('artistForm.avatar')}
                </label>
                {avatarPreview && (
                  <img src={avatarPreview} alt="" className="w-20 h-20 rounded-full mb-2 object-cover" />
                )}
                <input type="file" accept="image/*" onChange={handleAvatarChange} className="text-gray-300 text-sm" />
              </div>
            </>
          )}

          {!isInviteFlow && (
            <SimpleCaptcha onValidate={setCaptchaValid} />
          )}

          <div className="flex gap-3">
            {!isInviteFlow && (
              <Button type="button" variant="secondary" onClick={() => setStep('type')}>
                {t('artistForm.previous')}
              </Button>
            )}
            <Button type="submit" className="flex-1 justify-center" disabled={loading}>
              {loading ? '...' : isInviteFlow ? t('register.submitRegister') : t('register.submitApplication')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PublicRegister;
